import classNames from 'classnames';
import { flatMap, isEmpty, isUndefined } from 'lodash-es';
import { ArrowsDownUp } from 'phosphor-react';
import { useCallback, useMemo } from 'react';
import type { Sorting } from '../../table';
import { SortDirection } from '../../table';
import { ButtonActionQuietSurrogate } from '../button/button';
import type { Option } from '../form';
import { Label, Select } from '../form';

type SortingControllerProps<T extends string> = {
  onSortChange: (sorting: Sorting<T>) => void;
  sorting: Sorting<T>;
  sortOptions: Option<T>[];
  className?: string;
  expanded?: boolean;
  isMobile?: boolean;
  toggleExpandArea: () => void;
};

export function SortingController<T extends string>({ isMobile = false, toggleExpandArea, expanded = false, sorting, sortOptions, onSortChange, className }: SortingControllerProps<T>) {
  const separator = '--';

  const onChange = useCallback((value: string | undefined) => {
    if (isUndefined(value)) {
      onSortChange([]);
    } else {
      const [column, direction] = value.split(separator) as [NonNullable<typeof sorting[0]>, SortDirection];
      onSortChange([column, direction]);
    }
  }, [onSortChange]);

  const sortOptionsWithDirection = useMemo(() => {
    return flatMap(sortOptions, ({ label, value }) => [
      { label: `${label} A-Z`, value: `${value}${separator}${SortDirection.Ascending}` },
      { label: `${label} Z-A`, value: `${value}${separator}${SortDirection.Descending}` },
    ]);
  }, [sortOptions]);

  const sortingWithDirection = useMemo(() => {
    if (isEmpty(sorting)) {
      return undefined;
    }
    return `${sorting[0]}${separator}${sorting[1]}`;
  }, [sorting]);

  return (
    <>
      { !isMobile && <ButtonActionQuietSurrogate className="md:hidden" hideLabel icon={ArrowsDownUp} onClick={toggleExpandArea}/>}
      <Label className={classNames(className, { 'hidden md:flex': !isMobile })} label="Sortieren nach">
        <Select addEmptyOption={false} options={sortOptionsWithDirection} onValueChange={onChange} value={sortingWithDirection} variant="small"/>
      </Label>
    </>
  );
}
